import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { countUsers } from "../../../../services/userServices";
import { countEvents } from "../../../../services/eventServices";
import { countGroups } from "../../../../services/groupServices";
import { getTotalAchats } from "../../../../services/achatsServices";
import { getAllAdherentsList } from "../../../../services/adherentServices";
import {
  countVisitesByDate,
  deleteVisitesByDate,
} from "../../../../services/visitesServices";
import {
  deleteMessageContactForm,
  getAllMessagesContactForm,
  markMessageContactForm,
} from "../../../../services/contactformServices";

import { useNotification } from "../../../../components/Notification/NotificationManager";
import { departementsEtRegions } from "../../../../utils/departements";

import {
  DEFAULT_ABONNEMENT_STATS,
  DEFAULT_EVENT_STATS,
  DEFAULT_GROUP_STATS,
  DEFAULT_USER_STATS,
  buildDashboardKpis,
  normalizeAbonnementStats,
  normalizeEventStats,
  normalizeGroupStats,
  normalizeMessages,
  normalizeUserStats,
  prepareAdherentsGrowthData,
  prepareDepartmentData,
  prepareSubscriptionsData,
  prepareVisitsData,
} from "../utils/adminDashboardUtils";

export function useAdminDashboard() {
  const { addNotification } = useNotification();

  const notificationRef = useRef(addNotification);
  const hasLoadedOnceRef = useRef(false);

  const [userStats, setUserStats] = useState(DEFAULT_USER_STATS);
  const [eventStats, setEventStats] = useState(DEFAULT_EVENT_STATS);
  const [groupStats, setGroupStats] = useState(DEFAULT_GROUP_STATS);
  const [abonnementStats, setAbonnementStats] = useState(DEFAULT_ABONNEMENT_STATS);

  const [messages, setMessages] = useState([]);
  const [visitsData, setVisitsData] = useState([]);
  const [adherents, setAdherents] = useState([]);

  const [visitDeleteForm, setVisitDeleteForm] = useState({
    dateDebut: "",
    dateFin: "",
  });

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [deletingVisits, setDeletingVisits] = useState(false);
  const [messageActionId, setMessageActionId] = useState(null);

  useEffect(() => {
    notificationRef.current = addNotification;
  }, [addNotification]);

  const isDemoMode = useMemo(() => {
    try {
      const rawUser = localStorage.getItem("user") || localStorage.getItem("userInfo");
      const parsedUser = rawUser ? JSON.parse(rawUser) : null;

      return Number(parsedUser?.role_id) === 4;
    } catch {
      return false;
    }
  }, []);

  const fetchDashboard = useCallback(
    async ({ silent = false } = {}) => {
      const isInitialLoad = !hasLoadedOnceRef.current;

      if (isInitialLoad) {
        setLoading(true);
      } else {
        setRefreshing(true);
      }

      try {
        const [
          usersResponse,
          eventsResponse,
          groupsResponse,
          abonnementsResponse,
          messagesResponse,
          visitsResponse,
          adherentsResponse,
        ] = await Promise.all([
          countUsers(),
          countEvents(),
          countGroups(),
          getTotalAchats(),
          getAllMessagesContactForm(),
          countVisitesByDate(),
          getAllAdherentsList(),
        ]);

        setUserStats(normalizeUserStats(usersResponse));
        setEventStats(normalizeEventStats(eventsResponse));
        setGroupStats(normalizeGroupStats(groupsResponse));
        setAbonnementStats(normalizeAbonnementStats(abonnementsResponse));
        setMessages(normalizeMessages(Array.isArray(messagesResponse) ? messagesResponse : []));
        setVisitsData(prepareVisitsData(Array.isArray(visitsResponse) ? visitsResponse : []));
        setAdherents(Array.isArray(adherentsResponse) ? adherentsResponse : []);

        if (!silent && !isInitialLoad) {
          notificationRef.current?.("Tableau de bord actualisé.", "success");
        }
      } catch (error) {
        console.error("Erreur lors du chargement du dashboard admin :", error);

        notificationRef.current?.(
          "Erreur lors du chargement du tableau de bord.",
          "error",
        );
      } finally {
        hasLoadedOnceRef.current = true;
        setLoading(false);
        setRefreshing(false);
      }
    },
    [],
  );

  useEffect(() => {
    fetchDashboard({ silent: true });
  }, [fetchDashboard]);

  const handleVisitDeleteFormChange = useCallback((event) => {
    const { name, value } = event.target;

    setVisitDeleteForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  }, []);

  const handleDeleteVisits = useCallback(async () => {
    if (isDemoMode) {
      notificationRef.current?.(
        "Fonction désactivée en mode démonstration.",
        "info",
      );
      return;
    }

    if (!visitDeleteForm.dateDebut || !visitDeleteForm.dateFin) {
      notificationRef.current?.(
        "Sélectionnez une date de début et une date de fin.",
        "error",
      );
      return;
    }

    const confirmDelete = window.confirm(
      `Supprimer les visites entre le ${visitDeleteForm.dateDebut} et le ${visitDeleteForm.dateFin} ?`,
    );

    if (!confirmDelete) return;

    setDeletingVisits(true);

    try {
      await deleteVisitesByDate(visitDeleteForm.dateDebut, visitDeleteForm.dateFin);

      const updatedVisits = await countVisitesByDate();
      setVisitsData(prepareVisitsData(Array.isArray(updatedVisits) ? updatedVisits : []));

      setVisitDeleteForm({
        dateDebut: "",
        dateFin: "",
      });

      notificationRef.current?.("Visites supprimées avec succès.", "success");
    } catch (error) {
      console.error("Erreur lors de la suppression des visites :", error);

      notificationRef.current?.(
        "Erreur lors de la suppression des visites.",
        "error",
      );
    } finally {
      setDeletingVisits(false);
    }
  }, [isDemoMode, visitDeleteForm]);

  const handleToggleMessageRead = useCallback(
    async (messageId) => {
      if (isDemoMode) {
        notificationRef.current?.(
          "Fonction désactivée en mode démonstration.",
          "info",
        );
        return;
      }

      setMessageActionId(messageId);

      try {
        await markMessageContactForm(messageId);

        setMessages((prev) =>
          normalizeMessages(
            prev.map((message) =>
              message.id === messageId
                ? { ...message, read_message: !message.read_message }
                : message,
            ),
          ),
        );

        notificationRef.current?.("Message mis à jour.", "success");
      } catch (error) {
        console.error("Erreur lors du changement de statut du message :", error);

        notificationRef.current?.(
          "Erreur lors de la mise à jour du message.",
          "error",
        );
      } finally {
        setMessageActionId(null);
      }
    },
    [isDemoMode],
  );

  const handleDeleteMessage = useCallback(
    async (messageId) => {
      if (isDemoMode) {
        notificationRef.current?.(
          "Fonction désactivée en mode démonstration.",
          "info",
        );
        return;
      }

      const confirmDelete = window.confirm("Supprimer ce message ?");

      if (!confirmDelete) return;

      setMessageActionId(messageId);

      try {
        await deleteMessageContactForm(messageId);

        setMessages((prev) => prev.filter((message) => message.id !== messageId));

        notificationRef.current?.("Message supprimé.", "success");
      } catch (error) {
        console.error("Erreur lors de la suppression du message :", error);

        notificationRef.current?.(
          "Erreur lors de la suppression du message.",
          "error",
        );
      } finally {
        setMessageActionId(null);
      }
    },
    [isDemoMode],
  );

  const kpis = useMemo(
    () =>
      buildDashboardKpis({
        userStats,
        eventStats,
        groupStats,
        abonnementStats,
        messages,
        adherents,
      }),
    [abonnementStats, adherents, eventStats, groupStats, messages, userStats],
  );

  const adherentsGrowthData = useMemo(() => {
    return prepareAdherentsGrowthData(adherents);
  }, [adherents]);

  const departmentData = useMemo(() => {
    return prepareDepartmentData(adherents, departementsEtRegions);
  }, [adherents]);

  const subscriptionsData = useMemo(() => {
    return prepareSubscriptionsData(abonnementStats);
  }, [abonnementStats]);

  return {
    userStats,
    eventStats,
    groupStats,
    abonnementStats,
    messages,
    visitsData,
    adherents,
    adherentsGrowthData,
    departmentData,
    subscriptionsData,
    kpis,
    visitDeleteForm,
    loading,
    refreshing,
    deletingVisits,
    messageActionId,
    fetchDashboard,
    handleVisitDeleteFormChange,
    handleDeleteVisits,
    handleToggleMessageRead,
    handleDeleteMessage,
  };
}